import * as React from "react";
import { graphql, useFragment } from "react-relay";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { createSource, LegacySource } from "src/components/ui/SourceReference";

import { ChecklistToolCallResult_toolCall$key } from "./__generated__/ChecklistToolCallResult_toolCall.graphql";
import { TasksRenderer } from "./TasksRenderer";
import { ToolCallResultRenderer } from "./ToolCallResultRenderer";
import { parseCompleteTodoList, parseTodoWriteInput, processToolContent } from "./utils";

interface Task {
  task_num: number;
  title: string;
  description: string;
  status: string;
}

interface ChecklistToolCallResultProps {
  toolCall: ChecklistToolCallResult_toolCall$key;
}

const fragment = graphql`
  fragment ChecklistToolCallResult_toolCall on GraphQLMessageToolCall {
    toolUse {
      name
      displayName
      input {
        __typename
        ... on DatadogLogsToolInput { question }
        ... on DatadogLogsAggregationToolInput { question }
        ... on DatadogMetricsToolInput { question }
        ... on DatadogSpansToolInput { question }
        ... on DatadogSpansAggregationToolInput { question }
        ... on DatadogRumAggregationToolInput { question }
        ... on DatadogEventsToolInput { question }
        ... on DatadogCIPipelineEventsToolInput { question }
        ... on DatadogCIPipelineEventsAggregationToolInput { question }
        ... on DatadogMonitorToolInput { question }
        ... on DatadogServiceDependencyToolInput { question }
        ... on DatadogErrorTrackingIssueDetailsToolInput { question }
        ... on DatadogErrorTrackingIssuesToolInput { question }
        ... on BuildkiteBuildsToolInput { question }
        ... on BuildkiteLogsToolInput { question }
        ... on BuildkiteAnnotationsToolInput { question }
        ... on BuildkiteArtifactsToolInput { question }
        ... on BuildkiteFlakyTestToolInput { question }
        ... on CloudWatchLogsToolInput { question }
        ... on CloudWatchLogsAggregationToolInput { question }
        ... on CloudWatchMetricsToolInput { question }
        ... on NewRelicLogsToolInput { question }
        ... on NewRelicLogsAggregationToolInput { question }
        ... on NewRelicMetricsToolInput { question }
        ... on NewRelicSpansToolInput { question }
        ... on NewRelicSpansAggregationToolInput { question }
        ... on NewRelicTransactionsToolInput { question }
        ... on NewRelicTransactionsAggregationToolInput { question }
        ... on QueryCodeToolInput { question }
        ... on GitHubActionsRunsToolInput { question }
        ... on GitHubActionsLogsToolInput { question }
        ... on SlackChannelContentsToolInput { question }
        ... on SlackChannelMetadataToolInput { question }
        ... on SlackUsergroupMentionToolInput { usergroupHandle }
        ... on TemporalWorkflowsToolInput { question }
        ... on TemporalWorkflowHistoryToolInput { question }
        ... on UpdateTodoToolInput { tasks }
        ... on GenericToolInput { rawInput }
      }
    }
    toolResult {
      content
      sources {
        toolId
        title
        description
        type
        displayText
        artifactId
        groupTags
        query
        link

      }
      llmConversation {
        id
      }
    }
  }
`;

export const ChecklistToolCallResult: React.FC<ChecklistToolCallResultProps> = ({
  toolCall,
}) => {
  const data = useFragment(fragment, toolCall);

  const isUpdateTodoTool = data.toolUse?.name === "UpdateTodoTool";
  const isTodoWriteTool = data.toolUse?.name === "TodoWrite";
  const isTodoTool = isUpdateTodoTool || isTodoWriteTool;

  // For UpdateTodoTool, parse tasks from result
  // For TodoWrite, extract tasks from input rawInput
  const completeTodoList = isUpdateTodoTool
    ? parseCompleteTodoList(data.toolResult)
    : isTodoWriteTool && data.toolUse?.input?.__typename === "GenericToolInput"
    ? parseTodoWriteInput(data.toolUse.input)
    : null;
  const tasksToRender = completeTodoList;

  const shouldRenderAsJSX = isTodoTool && tasksToRender;
  const toolContent = !shouldRenderAsJSX ? processToolContent(data) : null;

  // Show loading spinner for UpdateTodoTool/TodoWrite while waiting for task data
  if (isTodoTool && !tasksToRender) {
    const hasToolResult = Boolean(data.toolResult);

    // Still loading - show spinner
    if (!hasToolResult) {
      return (
        <div className="mb-2 rounded-t transition-colors">
          <div className="flex items-center justify-between">
            <div className="font-semibold flex-1 min-w-0">
              <span className="text-primary min-w-0 break-words">
                ✦ Todos
              </span>
            </div>
          </div>
          <div className="border border-border p-2 mt-2 rounded-md space-y-1">
            <LoadingSpinner className="h-3.5 w-3.5 flex-shrink-0" />
          </div>
        </div>
      );
    }

    // Tool result exists but failed to parse - show error state
    return (
      <div className="mb-2 rounded-t transition-colors">
        <div className="flex items-center justify-between">
          <div className="font-semibold flex-1 min-w-0">
            <span className="text-primary min-w-0 break-words">
              ✦ Todos
            </span>
          </div>
        </div>
        <div className="border border-border p-2 mt-2 rounded-md text-xs text-muted-foreground">
          Unable to display todos for this tool result.
        </div>
      </div>
    );
  }

  const getTitle = () => {
    if (data.toolUse?.name === "UpdateTodoTool" || data.toolUse?.name === "TodoWrite") {
      return "Todos";
    }
    return "Research";
  };

  const getTitleDescription = () => {
    return "";
  };

  const getSources = () => {
    return data.toolResult?.sources?.map((s) => {
      const legacySource: LegacySource = {
        ...s,
        sourceId: s.toolId || "",
      };
      return createSource(legacySource);
    }) || [];
  };

  return (
    <div className="mb-2 rounded-t transition-colors">
      <div className="flex items-center justify-between">
        <div className="font-semibold flex-1 min-w-0">
          <span className="text-primary min-w-0 break-words">
            ✦ {getTitle()}
          </span>
          <span className="ml-2 text-muted-foreground min-w-0 break-words">
            {getTitleDescription()}
          </span>
        </div>
      </div>
      {shouldRenderAsJSX ? (
        <TasksRenderer
          tasks={tasksToRender as unknown as Task[]}
        />
      ) : data.toolResult?.llmConversation?.id ? (
        <ToolCallResultRenderer
          conversationId={data.toolResult.llmConversation.id}
          toolContent={toolContent || ""}
          sources={getSources()}
          subTaskTitle="Perform Task"
        />
      ) : (
        <div className="border border-border p-2 mt-2 rounded-md space-y-1">
          <LoadingSpinner className="h-3.5 w-3.5 flex-shrink-0" />
        </div>
      )}
    </div>
  );
};